<template>
    <section>
        <p class="govuk-!-margin-bottom-6">This calculator is CA marked as a class 1 device. See
            below for <a href="#regulatory-information"><strong>regulatory information</strong></a>.
        </p>
        <div id="calc">
            <!-- Intro Text -->
            <details class="govuk-details govuk-!-margin-bottom-3">
                <summary class="govuk-details__summary">
                    <span class="govuk-details__summary-text">
                        Please review the following information to determine whether IV fluid is
                        needed.
                    </span>
                </summary>
                <div class="govuk-details__text">
                    <div id="required-accordion-text">
                        <p>Clinical assessment should be accompanied by review of fluid balance
                            charts, NEWS chart and blood
                            results.</p>
                        <p><strong>No fluid likely to be required if patient is:</strong></p>
                        <ul>
                            <li>Drinking adequately</li>
                            <li>Receiving adequate fluid via nasogastric feed or total parenteral
                                nutrition</li>
                            <li>Receiving large volumes with drug infusions</li>
                        </ul>
                        <p><strong>Fluid likely to be required if patient:</strong></p>
                        <ul>
                            <li>Has not been drinking for 8h or more</li>
                            <li>Has lost or are losing fluid</li>
                            <li>Unable to meet daily fluid requirement with oral/enteral intake</li>
                        </ul>
                        <p><strong>Definitions:</strong></p>
                        <p><strong>Euvolaemic</strong> (normal fluid balance)</p>
                        <ul>
                            <li>Extremities are warm, blood pressure and heart rate are normal</li>
                        </ul>
                        <p><strong>Hypovolaemic</strong> (too little fluid)</p>
                        <ul>
                            <li>Cold extremities, hypotension, tachycardia, oliguria, fluid loss,
                                confusion</li>
                        </ul>
                        <p><strong>Hypervolaemic</strong> (too much fluid)</p>
                        <ul>
                            <li>Oedematous, inspiratory crackles; raised JVP, poor urine output or
                                fluid overload</li>
                        </ul>
                    </div>
                </div>
            </details>

            <div id="calculator-body">
                <!-- Error Summary-->
                <div id="error-summary-container">
                    <div class="govuk-error-summary" v-if="$v.form.$anyError"
                        data-module="govuk-error-summary">
                        <div role="alert">
                            <h2 class="govuk-error-summary__title">There is a problem</h2>
                            <div class="govuk-error-summary__body">
                                <ul class="govuk-list govuk-error-summary__list">
                                    <li class="govuk-!-margin-bottom-1"
                                        :id="weight.errors.errorSummary.id"
                                        v-if="$v.form.weightKg.$error || $v.form.weightSt.$error || $v.form.weightLb.$error">
                                        <a v-if="!$v.form.weightKg.required || !$v.form.weightSt.required || !$v.form.weightSt.numeric || !$v.form.weightKg.minValue || !$v.form.weightSt.minValue || !$v.form.weightLb.minValue"
                                            href="#weight-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{ weight.errors.required.message
                                            }}</strong></a>
                                        <a v-if="!$v.form.weightKg.decimalPlaces"
                                            href="#weight-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                weight.errors.metricDecimal.message }}</strong></a>
                                        <a v-if="!$v.form.weightLb.maxValue"
                                            href="#weight-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                weight.errors.maxPounds.message }}</strong></a>
                                        <a v-if="!$v.form.weightSt.containsDecimal"
                                            href="#weight-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                weight.errors.imperialDecimal.messageStart +
                                                'Stones' +
                                                weight.errors.imperialDecimal.messageEnd
                                            }}</strong></a>
                                        <a v-if="!$v.form.weightLb.containsDecimal"
                                            href="#weight-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                weight.errors.imperialDecimal.messageStart +
                                                'Pounds' +
                                                weight.errors.imperialDecimal.messageEnd
                                            }}</strong></a>
                                    </li>
                                    <li class="govuk-!-margin-bottom-1"
                                        :id="frailty.errors.errorSummary.id"
                                        v-if="$v.form.frailty.$error">
                                        <a class="govuk-!-display-inline-block"
                                            href="#frailty-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                frailty.errors.required.message }}</strong></a>
                                    </li>
                                    <li class="govuk-!-margin-bottom-1"
                                        :id="potassium.errors.errorSummary.id"
                                        v-if="$v.form.potassium.$error">
                                        <a class="govuk-!-display-inline-block"
                                            href="#potassium-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                potassium.errors.required.message }}</strong></a>
                                    </li>
                                    <li class="govuk-!-margin-bottom-1"
                                        :id="sodium.errors.errorSummary.id"
                                        v-if="$v.form.sodium.$error">
                                        <a class="govuk-!-display-inline-block"
                                            href="#sodium-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                sodium.errors.required.message }}</strong></a>
                                    </li>
                                    <li class="govuk-!-margin-bottom-1"
                                        :id="previousLosses.errors.errorSummary.id"
                                        v-if="$v.form.selectedLosses.$error">
                                        <a class="govuk-!-display-inline-block"
                                            href="#primary-loss-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                previousLosses.errors.required.message
                                            }}</strong></a>
                                    </li>
                                    <div :id="previousLosses.errors.conditionalErrorSummary.id"
                                        v-if="$v.form.lossVolume.$error">
                                        <div v-for="(item, index) in previousLosses.answers"
                                            :key="index + '-loss'">
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'loss-volume-required-' + index"
                                                v-if="!$v.form.lossVolume.$each[index].value.required">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#primary-loss-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        previousLosses.errors.conditionalRequired.message
                                                        + item.label
                                                    }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'loss-volume-range-' + index"
                                                v-if="(!$v.form.lossVolume.$each[index].value.maxValue || !$v.form.lossVolume.$each[index].value.minValue) && $v.form.lossVolume.$each[index].value.numeric || !$v.form.lossVolume.$each[index].value.containsNegative">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#primary-loss-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        previousLosses.errors.valueRange.messageStart
                                                        + item.label +
                                                        previousLosses.errors.valueRange.messageEnd
                                                    }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'loss-volume-numeric-' + index"
                                                v-if="!$v.form.lossVolume.$each[index].value.numeric && $v.form.lossVolume.$each[index].value.containsDecimal && $v.form.lossVolume.$each[index].value.containsNegative">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#primary-loss-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        previousLosses.errors.numeric.messageStart +
                                                        item.label +
                                                        previousLosses.errors.numeric.messageEnd
                                                    }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'loss-volume-decimal-' + index"
                                                v-if="!$v.form.lossVolume.$each[index].value.containsDecimal">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#primary-loss-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        previousLosses.errors.decimal.messageStart +
                                                        item.label +
                                                        previousLosses.errors.decimal.messageEnd
                                                    }}</strong></a>
                                            </li>
                                        </div>
                                    </div>
                                    <li :id="expectedIntakes.errors.errorSummary.id"
                                        v-if="$v.form.selectedIntakes.$error">
                                        <a href="#selected-intakes-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                expectedIntakes.errors.required.message
                                            }}</strong></a>
                                    </li>
                                    <div :id="expectedIntakes.errors.conditionalErrorSummary.id"
                                        v-if="$v.form.intakeVolume.$error">
                                        <div v-for="(item, index) in expectedIntakes.answers"
                                            :key="index + '-intake'">
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'intake-volume-required-' + index"
                                                v-if="!$v.form.intakeVolume.$each[index].value.required">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#selected-intakes-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        expectedIntakes.errors.conditionalRequired.message
                                                        +
                                                        item.label }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'intake-volume-range-' + index"
                                                v-if="(!$v.form.intakeVolume.$each[index].value.maxValue || !$v.form.intakeVolume.$each[index].value.minValue) && $v.form.intakeVolume.$each[index].value.numeric || !$v.form.intakeVolume.$each[index].value.containsNegative">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#selected-intakes-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        expectedIntakes.errors.valueRange.messageStart
                                                        + item.label +
                                                        expectedIntakes.errors.valueRange.messageEnd
                                                    }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'intake-volume-numeric-' + index"
                                                v-if="!$v.form.intakeVolume.$each[index].value.numeric && $v.form.intakeVolume.$each[index].value.containsDecimal && $v.form.intakeVolume.$each[index].value.containsNegative">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#selected-intakes-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        expectedIntakes.errors.numeric.messageStart
                                                        + item.label +
                                                        expectedIntakes.errors.numeric.messageEnd
                                                    }}</strong></a>
                                            </li>
                                            <li class="govuk-!-margin-bottom-1"
                                                :id="'intake-volume-decimal-' + index"
                                                v-if="!$v.form.intakeVolume.$each[index].value.containsDecimal">
                                                <a class="govuk-!-display-inline-block"
                                                    href="#selected-intakes-input"><span
                                                        class="govuk-visually-hidden">Error:
                                                    </span><strong>{{
                                                        expectedIntakes.errors.decimal.messageStart
                                                        + item.label +
                                                        expectedIntakes.errors.decimal.messageEnd
                                                    }}</strong></a>
                                            </li>
                                        </div>
                                    </div>
                                    <li :id="replacementFluids.errors.errorSummary.id"
                                        v-if="$v.form.replacementFluid.$error">
                                        <a href="#replacement-fluid-input"><span
                                                class="govuk-visually-hidden">Error:
                                            </span><strong>{{
                                                replacementFluids.errors.required.message
                                            }}</strong></a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form -->
                <div>
                    <!-- Inital prompt -->
                    <fieldset class="govuk-fieldset">
                        <div class="govuk-form-group">
                            <legend class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                <h2 class="govuk-fieldset__heading">{{ initialPrompt.title }}</h2>
                            </legend>
                            <div class="govuk-hint">
                                <div v-for="(item, index) in initialPrompt.subText" :key="index">
                                    <p><strong>{{ item.label }}</strong></p>
                                    <p>{{ item.introduction }}</p>
                                    <ul>
                                        <li v-for="(listItem, index2) in item.list" :key="index2">{{
                                            listItem }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <!-- What type of fluid does your patient require -->
                    <fieldset id="fluid-required" class="govuk-fieldset">
                        <div class="govuk-form-group">
                            <legend class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                <h2 class="govuk-fieldset__heading">{{ fluidRequired.title }}</h2>
                            </legend>

                            <div class="govuk-hint">
                                <p><strong>{{ fluidRequired.replacement.title }}</strong> {{
                                    fluidRequired.replacement.text
                                }}</p>
                                <p><strong>{{ fluidRequired.maintenance.title }}</strong> {{
                                    fluidRequired.maintenance.text
                                }}</p>
                                <p><strong>{{ fluidRequired.resuscitation.title }}</strong> {{
                                    fluidRequired.resuscitation.text }}</p>
                            </div>
                            <div class="govuk-radios">
                                <div v-for="(item, index) in fluidRequired.answers" :key="index"
                                    class="govuk-radios__item">
                                    <input class="govuk-radios__input"
                                        :id="fluidRequired.id + '-' + index"
                                        :name="fluidRequired.id" type="radio" :value="item"
                                        v-model="form.fluidRequired" />
                                    <label class="govuk-label govuk-radios__label"
                                        :for="fluidRequired.id + '-' + index">
                                        {{ item }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <div id="resuscitation" v-if="resuscitation">
                        <a class="govuk-button" :node-id="fluidRequired.resuscitation.nodeID"
                            :toolkit-id="fluidRequired.resuscitation.toolkitID"
                            :href="fluidRequired.resuscitation.linkUrl">
                            {{ fluidRequired.resuscitation.linkText }}</a>
                    </div>

                    <div id="replacement-and-maintenance-calculator"
                        v-if="replacementAndMaintenance">

                        <hr class="govuk-!-margin-bottom-6" />

                        <h2 class="govuk-!-margin-bottom-6">Replacement and maintenance calculator:
                        </h2>

                        <!-- Weight -->
                        <fieldset :id="weight.id + '-input'" class="govuk-fieldset">
                            <div
                                :class="{ 'govuk-form-group--error': $v.form.weightKg.$anyError || $v.form.weightSt.$anyError || $v.form.weightLb.$anyError }">
                                <legend class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                    <h2 class="govuk-fieldset__heading">{{ weight.title }}</h2>
                                </legend>
                                <div class="govuk-hint">
                                    <p>{{ weight.subText }}</p>
                                </div>
                                <div :id="weight.errorId" class="govuk-error-message"
                                    v-if="$v.form.weightKg.$anyError || $v.form.weightSt.$anyError || $v.form.weightLb.$anyError">
                                    <p v-if="!$v.form.weightKg.required || !$v.form.weightSt.required || !$v.form.weightLb.required || !$v.form.weightSt.numeric || !$v.form.weightKg.minValue || !$v.form.weightSt.minValue || !$v.form.weightLb.minValue"
                                        :id="weight.errors.required.id"><span
                                            class="govuk-visually-hidden">Error: </span>{{
                                                weight.errors.required.message }}</p>
                                    <p v-if="!$v.form.weightKg.decimalPlaces"
                                        :id="weight.errors.metricDecimal.id"><span
                                            class="govuk-visually-hidden">Error: </span>{{
                                                weight.errors.metricDecimal.message }}</p>
                                    <p v-if="!$v.form.weightLb.maxValue"
                                        :id="weight.errors.maxPounds.id"><span
                                            class="govuk-visually-hidden">Error: </span>{{
                                                weight.errors.maxPounds.message
                                            }}</p>
                                    <p v-if="!$v.form.weightSt.containsDecimal"
                                        :id="weight.errors.imperialDecimal.id + 'st'"><span
                                            class="govuk-visually-hidden">Error: </span>{{
                                                weight.errors.imperialDecimal.messageStart + 'Stones' +
                                                weight.errors.imperialDecimal.messageEnd }}</p>
                                    <p v-if="!$v.form.weightLb.containsDecimal"
                                        :id="weight.errors.imperialDecimal.id + 'lb'"><span
                                            class="govuk-visually-hidden">Error: </span>{{
                                                weight.errors.imperialDecimal.messageStart + 'Pounds' +
                                                weight.errors.imperialDecimal.messageEnd }}</p>
                                </div>
                                <div>
                                    <a :id="weight.button.id" @click="swapWeightUnit">{{
                                        weightButtonText }}</a>
                                </div>
                                <div v-if="(displayMetricWarning || displayImperialWarning) && form.weightEdited"
                                    :id="weight.warningId"
                                    class="govuk-inset-text govuk-inset-text--warning govuk-!-margin-top-3 govuk-!-margin-bottom-3">
                                    <div v-if="!swapToImperial"
                                        :id="weight.warnings.weightRangeMetric.id"
                                        class="govuk-warning-text">
                                        <span class="govuk-warning-text__icon"
                                            aria-hidden="true">!</span><strong
                                            class="govuk-warning-text__text"><span
                                                class="govuk-warning-text__assistive">Warning</span>{{
                                                    weight.warnings.weightRangeMetric.message }}</strong>
                                    </div>
                                    <div v-if="swapToImperial"
                                        :id="weight.warnings.weightRangeImperial.id"
                                        class="govuk-warning-text">
                                        <span class="govuk-warning-text__icon"
                                            aria-hidden="true">!</span><strong
                                            class="govuk-warning-text__text"><span
                                                class="govuk-warning-text__assistive">Warning</span>{{
                                                    weight.warnings.weightRangeImperial.message }}</strong>
                                    </div>
                                </div>
                                <div :id="weight.metric.containerId" class="govuk-form-group"
                                    v-if="!swapToImperial">
                                    <div class="govuk-form-group__kg govuk-!-margin-top-3">
                                        <label class="govuk-label" :for="weight.metric.id">
                                            {{ weight.metric.title }}
                                        </label>
                                        <div class="govuk-input__wrapper">
                                            <input class="govuk-input govuk-!-width-one-quarter"
                                                :id="weight.metric.id" :name="weight.metric.id"
                                                type="text" inputmode="decimal" pattern="[0-9]*"
                                                min="0" :decimal="false" :step="0.05"
                                                spellcheck="false" v-model="form.weightKg"
                                                @blur="kgToStPo()" />
                                            <div class="govuk-input__suffix" aria-hidden="true">{{
                                                weight.metric.unit }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div :id="weight.imperial.containerId" class="govuk-form-group"
                                    v-if="swapToImperial">
                                    <div class="govuk-form-group__st-and-po">
                                        <div class="govuk-form-input__stones govuk-!-margin-top-3">
                                            <label class="govuk-label"
                                                :for="weight.imperial.stones.id">
                                                {{ weight.imperial.stones.title }}
                                            </label>
                                            <div class="govuk-input__wrapper">
                                                <input class="govuk-input govuk-!-width-two-thirds"
                                                    :id="weight.imperial.stones.id"
                                                    :name="weight.imperial.stones.id" type="text"
                                                    inputmode="numeric" pattern="[0-9]*" min="0"
                                                    :decimal="false" :step="1" spellcheck="false"
                                                    v-model="form.weightSt" @blur="stPoToKg()" />
                                                <div class="govuk-input__suffix" aria-hidden="true">
                                                    {{
                                                        weight.imperial.stones.unit }}</div>
                                            </div>
                                        </div>

                                        <div class="govuk-form-input__pounds govuk-!-margin-top-3">
                                            <label class="govuk-label"
                                                :for="weight.imperial.pounds.id">
                                                {{ weight.imperial.pounds.title }}
                                            </label>
                                            <div class="govuk-input__wrapper">
                                                <input class="govuk-input govuk-!-width-two-thirds"
                                                    :id="weight.imperial.pounds.id"
                                                    :name="weight.imperial.pounds.id" type="number"
                                                    inputmode="numeric" min="0" max="13"
                                                    :decimal="false" :step="1" spellcheck="false"
                                                    v-model="form.weightLb" @blur="stPoToKg()" />
                                                <div class="govuk-input__suffix" aria-hidden="true">
                                                    {{
                                                        weight.imperial.pounds.unit }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>


                        <!-- Frialty present -->
                        <fieldset id="frailty-input" class="govuk-fieldset">
                            <div :class="{ 'govuk-form-group--error': $v.form.frailty.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{ frailty.title }}</h2>
                                    </legend>
                                    <div class="govuk-hint">
                                        <p>{{ frailty.subText }} <a :href="frailty.linkUrl"
                                                target="_blank">{{
                                                    frailty.linkText }}</a></p>
                                    </div>
                                    <div :id="frailty.errorId" class="govuk-error-message"
                                        v-if="$v.form.frailty.$anyError">
                                        <p v-if="!$v.form.frailty.required"
                                            :id="frailty.errors.required.id"><span
                                                class="govuk-visually-hidden">Error: </span>{{
                                                    frailty.errors.required.message }}</p>
                                    </div>
                                    <div class="govuk-radios">
                                        <div v-for="(item, index) in frailty.answers" :key="index"
                                            class="govuk-radios__item">
                                            <input class="govuk-radios__input"
                                                :id="frailty.id + '-' + index" :name="frailty.id"
                                                type="radio" :value="item" v-model="form.frailty" />
                                            <label class="govuk-label govuk-radios__label"
                                                :for="frailty.id + '-' + index">
                                                {{ item }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Potassium -->
                        <fieldset id="potassium-input" class="govuk-fieldset">
                            <div
                                :class="{ 'govuk-form-group--error': $v.form.potassium.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{ potassium.title }}
                                        </h2>
                                    </legend>
                                    <div :id="potassium.errorId" class="govuk-error-message"
                                        v-if="$v.form.potassium.$anyError">
                                        <p v-if="!$v.form.potassium.required"
                                            :id="potassium.errors.required.id"><span
                                                class="govuk-visually-hidden">Error: </span>{{
                                                    potassium.errors.required.message }}</p>
                                    </div>
                                    <div class="govuk-radios">
                                        <div v-for="(item, index) in potassium.answers" :key="index"
                                            class="govuk-radios__item">
                                            <input class="govuk-radios__input"
                                                :id="potassium.id + '-' + index"
                                                :name="potassium.title" type="radio" :value="item"
                                                v-model="form.potassium" />
                                            <label class="govuk-label govuk-radios__label"
                                                :for="potassium.id + '-' + index">
                                                {{ item }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Sodium -->
                        <fieldset id="sodium-input" class="govuk-fieldset">
                            <div :class="{ 'govuk-form-group--error': $v.form.sodium.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{ sodium.title }}</h2>
                                    </legend>
                                    <div :id="sodium.errorId" class="govuk-error-message"
                                        v-if="$v.form.sodium.$anyError">
                                        <p v-if="!$v.form.sodium.required"
                                            :id="sodium.errors.required.id"><span
                                                class="govuk-visually-hidden">Error: </span>{{
                                                    sodium.errors.required.message }}</p>
                                    </div>
                                    <div class="govuk-radios">
                                        <div v-for="(item, index) in sodium.answers" :key="index"
                                            class="govuk-radios__item">
                                            <input class="govuk-radios__input"
                                                :id="sodium.id + '-' + index" :name="sodium.title"
                                                type="radio" :value="item" v-model="form.sodium" />
                                            <label class="govuk-label govuk-radios__label"
                                                :for="sodium.id + '-' + index">
                                                {{ item }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Previous 24h losses to be replaced -->
                        <fieldset id="primary-loss-input" class="govuk-fieldset">
                            <div
                                :class="{ 'govuk-form-group--error': $v.form.selectedLosses.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{ previousLosses.title
                                        }}</h2>
                                    </legend>
                                    <div class="govuk-hint">
                                        <p v-for="(text, id) in previousLosses.subText" :key="id">{{
                                            text }}</p>
                                    </div>
                                    <div :id="previousLosses.errorId" class="govuk-error-message"
                                        v-if="$v.form.selectedLosses.$anyError">
                                        <p v-if="!$v.form.selectedLosses.required"
                                            :id="previousLosses.errors.required.id">
                                            <span class="govuk-visually-hidden">Error: </span>{{
                                                previousLosses.errors.required.message }}
                                        </p>
                                    </div>
                                    <div class="govuk-checkboxes" data-module="govuk-checkboxes">
                                        <div class="govuk-!-margin-bottom-2"
                                            v-for="(item, index) in previousLosses.answers"
                                            :key="index">
                                            <div class="govuk-checkboxes__item">
                                                <input class="govuk-checkboxes__input"
                                                    :id="previousLosses.id + '-' + index"
                                                    :name="previousLosses.id" type="checkbox"
                                                    @change="selectPreviousLoss()"
                                                    :value="item.label"
                                                    :data-aria-controls="'previousLosses-conditional-' + item.textInputId"
                                                    v-model="form.selectedLosses" />
                                                <label class="govuk-label govuk-checkboxes__label"
                                                    :for="previousLosses.id + '-' + index">
                                                    {{ item.label }}
                                                </label>
                                            </div>
                                            <div v-if="form.lossVolume[index].selected"
                                                class="govuk-checkboxes__conditional"
                                                :id="'previousLosses-conditional-' + item.textInputId">
                                                <div :class="{ 'govuk-form-group--error': $v.form.lossVolume.$anyError }"
                                                    class="govuk-form-group">
                                                    <label class="govuk-label"
                                                        :for="'previousLosses-conditional-' + index">
                                                        {{ item.textLabel }}
                                                    </label>
                                                    <div :id="previousLosses.conditionalErrorId + index"
                                                        class="govuk-error-message"
                                                        v-if="$v.form.lossVolume.$each[index].value.$anyError">
                                                        <p v-if="!$v.form.lossVolume.$each[index].value.required"
                                                            :id="previousLosses.errors.conditionalRequired.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                previousLosses.errors.conditionalRequired.message
                                                                + item.label
                                                            }}
                                                        </p>
                                                        <p v-if="(!$v.form.lossVolume.$each[index].value.maxValue || !$v.form.lossVolume.$each[index].value.minValue) && $v.form.lossVolume.$each[index].value.numeric || !$v.form.lossVolume.$each[index].value.containsNegative"
                                                            :id="previousLosses.errors.valueRange.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                previousLosses.errors.valueRange.messageStart
                                                                + item.label +
                                                                previousLosses.errors.valueRange.messageEnd
                                                            }}
                                                        </p>
                                                        <p v-if="!$v.form.lossVolume.$each[index].value.numeric && $v.form.lossVolume.$each[index].value.containsDecimal && $v.form.lossVolume.$each[index].value.containsNegative"
                                                            :id="previousLosses.errors.numeric.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                previousLosses.errors.numeric.messageStart
                                                                + item.label +
                                                                previousLosses.errors.numeric.messageEnd
                                                            }}
                                                        </p>
                                                        <p v-if="!$v.form.lossVolume.$each[index].value.containsDecimal"
                                                            :id="previousLosses.errors.decimal.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                previousLosses.errors.decimal.messageStart
                                                                + item.label +
                                                                previousLosses.errors.decimal.messageEnd
                                                            }}
                                                        </p>
                                                    </div>
                                                    <div class="govuk-input__wrapper">
                                                        <input
                                                            class="govuk-input govuk-!-width-one-quarter"
                                                            :id="'previousLosses-conditional-' + index"
                                                            @change="inputChanged()"
                                                            name="previousLossesConditional"
                                                            type="text" inputmode="numeric"
                                                            pattern="[0-9]*" spellcheck="false"
                                                            v-model="form.lossVolume[index].value" />
                                                        <div class="govuk-input__suffix"
                                                            aria-hidden="true">ml</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="govuk-checkboxes__divider">or</div>
                                        <div class="govuk-checkboxes__item">
                                            <input class="govuk-checkboxes__input"
                                                :id="previousLosses.id + '-' + (previousLosses.answers.length + 1)"
                                                :name="previousLosses.id" type="checkbox"
                                                :value="previousLosses.noLoss.label"
                                                v-model="form.selectedLosses"
                                                @change="selectPreviousLoss()">
                                            <label class="govuk-label govuk-checkboxes__label"
                                                :for="previousLosses.id + '-' + (previousLosses.answers.length + 1)">
                                                {{ previousLosses.noLoss.label }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Expected 24h intake - estimate -->
                        <fieldset id="selected-intakes-input" class="govuk-fieldset">
                            <div
                                :class="{ 'govuk-form-group--error': $v.form.selectedIntakes.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{ expectedIntakes.title
                                        }}</h2>
                                    </legend>
                                    <div class="govuk-hint">
                                        <p v-for="(text, id) in expectedIntakes.subText" :key="id">
                                            {{ text }}</p>
                                    </div>
                                    <div :id="expectedIntakes.errorId" class="govuk-error-message"
                                        v-if="$v.form.selectedIntakes.$anyError">
                                        <p v-if="!$v.form.selectedIntakes.required"
                                            :id="expectedIntakes.errors.required.id"><span
                                                class="govuk-visually-hidden">Error: </span>{{
                                                    expectedIntakes.errors.required.message }}</p>
                                    </div>
                                    <div class="govuk-checkboxes" data-module="govuk-checkboxes">
                                        <div class="govuk-!-margin-bottom-2"
                                            v-for="(item, index) in expectedIntakes.answers"
                                            :key="index">
                                            <div class="govuk-checkboxes__item">
                                                <input class="govuk-checkboxes__input"
                                                    :id="expectedIntakes.id + '-' + index"
                                                    :name="expectedIntakes.id" type="checkbox"
                                                    :value="item.label"
                                                    :data-aria-controls="'expected-intake-' + item.textInputId"
                                                    v-model="form.selectedIntakes"
                                                    @change="selectExpectedIntake()" />
                                                <label class="govuk-label govuk-checkboxes__label"
                                                    :for="expectedIntakes.id + '-' + index">
                                                    {{ item.label }}
                                                </label>
                                            </div>
                                            <div v-if="form.intakeVolume[index].selected"
                                                class="govuk-checkboxes__conditional"
                                                :id="'expected-' + item.textInputId">
                                                <div :class="{ 'govuk-form-group--error': $v.form.intakeVolume.$each[index].value.$anyError }"
                                                    class="govuk-form-group">
                                                    <label class="govuk-label"
                                                        :for="'expected-intake-conditional-' + index">
                                                        {{ item.textLabel }}
                                                    </label>
                                                    <div :id="expectedIntakes.conditionalErrorId + index"
                                                        class="govuk-error-message"
                                                        v-if="$v.form.intakeVolume.$each[index].value.$anyError">
                                                        <p v-if="!$v.form.intakeVolume.$each[index].value.required"
                                                            :id="expectedIntakes.errors.conditionalRequired.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                expectedIntakes.errors.conditionalRequired.message
                                                                + item.label
                                                            }}
                                                        </p>
                                                        <p v-if="((!$v.form.intakeVolume.$each[index].value.maxValue || !$v.form.intakeVolume.$each[index].value.minValue) && $v.form.intakeVolume.$each[index].value.numeric || !$v.form.intakeVolume.$each[index].value.containsNegative)"
                                                            :id="expectedIntakes.errors.valueRange.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                expectedIntakes.errors.valueRange.messageStart
                                                                + item.label +
                                                                expectedIntakes.errors.valueRange.messageEnd
                                                            }}
                                                        </p>
                                                        <p v-if="!$v.form.intakeVolume.$each[index].value.numeric && $v.form.intakeVolume.$each[index].value.containsDecimal && $v.form.intakeVolume.$each[index].value.containsNegative"
                                                            :id="expectedIntakes.errors.numeric.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                expectedIntakes.errors.numeric.messageStart
                                                                + item.label +
                                                                expectedIntakes.errors.numeric.messageEnd
                                                            }}
                                                        </p>
                                                        <p v-if="!$v.form.intakeVolume.$each[index].value.containsDecimal"
                                                            :id="expectedIntakes.errors.decimal.id + index">
                                                            <span
                                                                class="govuk-visually-hidden">Error:
                                                            </span>{{
                                                                expectedIntakes.errors.decimal.messageStart
                                                                + item.label +
                                                                expectedIntakes.errors.decimal.messageEnd
                                                            }}
                                                        </p>
                                                    </div>
                                                    <div class="govuk-input__wrapper">
                                                        <input
                                                            class="govuk-input govuk-!-width-one-quarter"
                                                            :id="'expected-intake-conditional-' + index"
                                                            @change="inputChanged()"
                                                            name="expectedIntakeConditional"
                                                            type="text" inputmode="numeric"
                                                            pattern="[0-9]*" spellcheck="false"
                                                            v-model="form.intakeVolume[index].value" />
                                                        <div class="govuk-input__suffix"
                                                            aria-hidden="true">ml</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="govuk-checkboxes__divider">or</div>
                                        <div class="govuk-checkboxes__item">
                                            <input class="govuk-checkboxes__input"
                                                :id="expectedIntakes.id + '-' + (expectedIntakes.answers.length + 1)"
                                                :name="expectedIntakes.id" type="checkbox"
                                                :value="expectedIntakes.noIntake.label"
                                                v-model="form.selectedIntakes"
                                                @change="selectExpectedIntake()">
                                            <label class="govuk-label govuk-checkboxes__label"
                                                :for="expectedIntakes.id + '-' + (expectedIntakes.answers.length + 1)">
                                                {{ expectedIntakes.noIntake.label }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Select replacement fluid used in your organisation -->
                        <fieldset v-if="displayReplacementFluid" id="replacement-fluid-input"
                            class="govuk-fieldset">
                            <div
                                :class="{ 'govuk-form-group--error': $v.form.replacementFluid.$anyError }">
                                <div class="govuk-form-group">
                                    <legend
                                        class="govuk-fieldset__legend govuk-fieldset__legend--m">
                                        <h2 class="govuk-fieldset__heading">{{
                                            replacementFluids.title }}</h2>
                                    </legend>
                                    <div :id="replacementFluids.errorId" class="govuk-error-message"
                                        v-if="$v.form.replacementFluid.$anyError">
                                        <p v-if="!$v.form.replacementFluid.required"
                                            :id="replacementFluids.errors.required.id"><span
                                                class="govuk-visually-hidden">Error: </span>{{
                                                    replacementFluids.errors.required.message }}</p>
                                    </div>
                                    <div class="govuk-radios">
                                        <div v-for="(item, index) in replacementFluids.answers"
                                            :key="index" class="govuk-radios__item">
                                            <input class="govuk-radios__input"
                                                :id="replacementFluids.id + '-' + index"
                                                :name="replacementFluids.id" type="radio"
                                                :value="item" v-model="form.replacementFluid" />
                                            <label class="govuk-label govuk-radios__label"
                                                :for="replacementFluids.id + '-' + index">
                                                {{ item }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>

                <!-- Result-->
                <div v-if="replacementAndMaintenance">
                    <!-- Submit button -->
                    <button @click="validateForm" class="govuk-button govuk-!-margin-right-3"
                        id="calculate-score" title="Click here to calculate score">
                        Calculate
                    </button>

                    <!-- Reset button -->
                    <button @click="reset" v-if="showClearButton" class="govuk-button"
                        id="reset-score" title="Click here to reset the form">
                        Clear
                    </button>
                </div>

                <div v-if="!errorSummary && formSubmitted">
                    <h2>IV fluid requirements</h2>
                    <h3>Suggested IV fluid over next 24hrs</h3>
                    <div id="replacement-output" class="calc-output__container"
                        v-if="this.form.selectedLosses.indexOf(this.previousLosses.noLoss.label) === -1">
                        <div class="govuk-summary-card">
                            <div class="govuk-summary-card__title-wrapper">
                                <h3 class="govuk-summary-card__title govuk-!-margin-0">
                                    Replacement
                                </h3>
                            </div>

                            <div class="govuk-summary-card__content">
                                <dl class="govuk-summary-list govuk-!-padding-3">
                                    <div class="govuk-summary-list__row"
                                        v-if="replacementCalculation.alerts.length > 0">
                                        <dt class="govuk-summary-list__key">
                                            <span class="calc-output__alert">
                                                <i class="fa-solid fa-triangle-exclamation"></i>
                                                Alerts
                                            </span>
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            <ul
                                                class="govuk-!-padding-left-6 govuk-!-margin-bottom-3">
                                                <li class="govuk-!-margin-bottom-4"
                                                    v-for="(alert, index) in replacementCalculation.alerts"
                                                    :key="index">
                                                    <div v-if="alert.length > 1">
                                                        <strong>{{ alert[0] }}</strong><br>
                                                        <span v-html="alert[1]"></span>
                                                    </div>
                                                    <div v-else>
                                                        <span v-html="alert[0]"></span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Fluid
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            <span>{{ replacementCalculation.fluidType }}</span>
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Volume
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            {{ replacementCalculation.fluidVolume }} ml
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Rate of administration (ml/hr)
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            <ul>
                                                <li v-for="(rate, index) in replacementCalculation.fluidRate"
                                                    :key="index">{{ rate }}</li>
                                            </ul>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div id="maintenance-output" class="calc-output__container">
                        <div class="govuk-summary-card">
                            <div class="govuk-summary-card__title-wrapper">
                                <h3 class="govuk-summary-card__title govuk-!-margin-0">
                                    Maintenance
                                </h3>
                            </div>

                            <div class="govuk-summary-card__content">
                                <dl class="govuk-summary-list govuk-!-padding-3">
                                    <div class="govuk-summary-list__row"
                                        v-if="maintenanceCalculation.alerts.length > 0">
                                        <dt class="govuk-summary-list__key">
                                            <span class="calc-output__alert">
                                                <i class="fa-solid fa-triangle-exclamation"></i>
                                                Alerts
                                            </span>
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            <ul
                                                class="govuk-!-padding-left-6 govuk-!-margin-bottom-3">
                                                <li class="govuk-!-margin-bottom-4"
                                                    v-for="(alert, index) in maintenanceCalculation.alerts"
                                                    :key="index">
                                                    <div v-if="alert.length > 1">
                                                        <strong>{{ alert[0] }}</strong><br>
                                                        <span v-html="alert[1]"></span>
                                                    </div>
                                                    <div v-else>
                                                        <span v-html="alert[0]"></span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Fluid
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            <span>{{ maintenanceCalculation.fluidType }}</span>
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Volume
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            {{ maintenanceCalculation.fluidVolume }} ml
                                        </dd>
                                    </div>
                                    <div class="govuk-summary-list__row">
                                        <dt class="govuk-summary-list__key">
                                            Rate of administration (ml/hr)
                                        </dt>
                                        <dd class="govuk-summary-list__value">
                                            {{ maintenanceCalculation.fluidRate }} ml/hr
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div id="information-output" class="calc-output__container">
                        <div class="govuk-summary-card">
                            <div class="govuk-summary-card__title-wrapper">
                                <h3 class="govuk-summary-card__title govuk-!-margin-0">
                                    Information
                                </h3>
                            </div>
                            <div class="govuk-summary-list__row">
                                <ul>
                                    <li v-for="(info, index) in results.info" :key="index">{{ info
                                    }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>



            </div>
        </div>

        <accordion id="regulatory-information">
            <template v-slot:title>Regulatory Information</template>
            <template v-slot:content>
                <p><strong>Product Name:</strong> Calculator Suite</p>
                <p><strong>Calculator Name:</strong> IV fluids calculator</p>
                <p><strong>Calculator Version:</strong> version 1.1.0</p>

                <div class="govuk-grid-row">
                    <div class="govuk-grid-column-two-thirds">
                        <div class="govuk-grid-column-one-quarter">
                            <img src="data:image/png;base64,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"
                                alt="Scottish Health Innovations Limited">
                        </div>
                        <div class="govuk-grid-column-one-quarter">
                            <p>
                                Innoscot Health<br>
                                Suite 23, The Pentagon Centre<br>
                                36 Washington Street<br>
                                Glasgow<br>
                                G3 8AZ
                            </p>
                        </div>
                    </div>
                </div>
                <div class="govuk-grid-row">
                    <div class="govuk-grid-column-two-thirds">
                        <div class="govuk-grid-column-one-quarter">
                            <p>
                                <img src="../assets/cemark/ukca.png" alt="UK Conformity Assessed">
                            </p>
                        </div>
                    </div>
                </div>
                <div class="govuk-grid-row">
                    <div class="govuk-grid-column-two-thirds">
                        <div class="govuk-grid-column-one-quarter">
                            <img src="../assets/cemark/warning.png" alt="Warning">
                        </div>
                        <div class="govuk-grid-column-three-quarters">
                            <p>
                                <strong>Calculators are <u>not</u> intended to be used by members of
                                    the public as a
                                    substitute for professional advice.</strong>
                            </p>
                        </div>
                    </div>
                </div>
                <div class="govuk-grid-row">
                    <div class="govuk-grid-column-two-thirds">
                        <div class="govuk-grid-column-one-quarter">
                            <img src="../assets/cemark/info.png"
                                alt="More Information on Right Decision" />
                        </div>
                        <div class="govuk-grid-column-one-quarter">
                            <p>
                                <a href="https://rightdecisions.scot.nhs.uk/iv-fluids-prescribing-calculator-and-guidance/instructions-for-use/"
                                    target="_blank">Instructions for use</a>
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </accordion>
    </section>
</template>

<script>
import "isomorphic-fetch";
import "details-element-polyfill";
import { maxValue, minValue, numeric, required, requiredIf } from "vuelidate/lib/validators";

import initialPrompt from "../assets/data/initialPrompt.json"
import fluidRequired from "../assets/data/fluidRequired.json"
import weight from "../assets/data/weight.json"
import frailty from "../assets/data/frailty.json";
import potassium from "../assets/data/potassium.json";
import sodium from "../assets/data/sodium.json";
import previousLosses from "../assets/data/previousLosses.json";
import expectedIntakes from "../assets/data/expectedIntakes.json";
import replacementFluids from "../assets/data/replacementFluids.json"
import results from "../assets/data/results.json";
import informationText from "../assets/data/informationalText.json";
import * as customValidation from "../assets/validators.js"

export default {
    name: "Calculator",
    data() {
        return {
            initialPrompt: initialPrompt,
            fluidRequired: fluidRequired,
            weight: weight,
            frailty: frailty,
            potassium: potassium,
            sodium: sodium,
            previousLosses: previousLosses,
            expectedIntakes: expectedIntakes,
            replacementFluids: replacementFluids,
            informationText: informationText,
            results: results,

            hoursInDay: 24,
            maxFluidRate: 100,
            replacementFluidThreshold: 500,
            weightThreshold: 80,

            formSubmitted: false,
            errorSummary: false,
            weightError: false,
            clearButton: false,
            swapToImperial: false,
            weightButtonText: weight.button.metricText,

            form: {
                fluidRequired: null,

                weightKg: null,
                weightSt: null,
                weightLb: null,
                weightNotValid: false,
                weightEdited: false,

                frailty: null,
                potassium: null,
                sodium: null,

                selectedLosses: [],
                lossVolume: [],

                selectedIntakes: [],
                intakeVolume: [],

                replacementFluid: null,
            },
            maintenanceCalculation: {
                volumeMultiplier: null,
                volumeMaximum: null,
                totalIntakeVolume: null,
                fluidType: null,
                fluidVolume: null,
                fluidRate: null,
                fluidDeliveryPeriod: null,
                alerts: [],
            },
            replacementCalculation: {
                lossesMaximum: null,
                fluidType: null,
                fluidVolume: null,
                fluidRate: [],
                alerts: [],
            }
        };
    },
    created() {
        this.form.lossVolume = this.emptyLossesArray();
        this.form.intakeVolume = this.emptyIntakesArray();
    },
    // Form Validation Properties
    validations: {
        form: {
            weightKg: {
                required,
                decimalPlaces: customValidation.maxDecimalPlaces(2),
                minValue: minValue(0)
            },
            weightSt: {
                required,
                containsDecimal: customValidation.contains('.'),
                numeric: function () { return !isNaN(this.form.weightSt) },
                minValue: minValue(0)
            },
            weightLb: {
                maxValue: maxValue(weight.errors.maxPounds.maxLb),
                containsDecimal: customValidation.contains('.'),
                minValue: minValue(0)
            },
            frailty: { required },
            sodium: { required },
            potassium: { required },
            selectedLosses: { required },
            lossVolume: {
                $each: {
                    selected: {},
                    value: {
                        required: requiredIf(function (volume) {
                            return volume.selected;
                        }),
                        maxValue: maxValue(previousLosses.errors.valueRange.maxValue),
                        minValue: minValue(previousLosses.errors.valueRange.minValue),
                        numeric,
                        containsDecimal: customValidation.contains('.'),
                        containsNegative: customValidation.contains('-')
                    }
                }
            },
            selectedIntakes: { required },
            intakeVolume: {
                $each: {
                    selected: {},
                    value: {
                        required: requiredIf(function (volume) {
                            return volume.selected;
                        }),
                        maxValue: maxValue(expectedIntakes.errors.valueRange.maxValue),
                        minValue: minValue(expectedIntakes.errors.valueRange.minValue),
                        numeric,
                        containsDecimal: customValidation.contains('.'),
                        containsNegative: customValidation.contains('-')
                    }
                }
            },
            replacementFluid: {
                required: requiredIf(function () {
                    return !this.replacementFluidHidden();
                })
            },
        }
    },
    // On the fly computed responses
    computed: {
        showClearButton() {
            let showClearButton = this.form.weightKg != null || this.form.weightSt != null || this.form.weightLb != null || this.form.frailty != null || this.form.potassium != null
                || this.form.sodium != null || this.form.selectedLosses.length > 0 || this.form.selectedIntakes.length > 0 || this.form.replacementFluid != null;
            this.setClearButton(showClearButton)
            return this.clearButton;
        },
        displayMetricWarning() {
            return (this.form.weightKg < weight.warnings.weightRangeMetric.minKg || this.form.weightKg > weight.warnings.weightRangeMetric.maxKg)
                && !isNaN(this.form.weightKg);
        },
        displayImperialWarning() {
            return ((this.form.weightSt <= weight.warnings.weightRangeMetric.minSt && this.form.weightLb < weight.warnings.weightRangeMetric.minLb
                || this.form.weightSt >= weight.warnings.weightRangeMetric.maxSt && this.form.weightLb > weight.warnings.weightRangeMetric.maxLb)
                || isNaN(this.form.weightSt))
                && !isNaN(this.form.weightKg);
        },
        replacementAndMaintenance() {
            return this.form.fluidRequired === fluidRequired.answers.replacementMaintenance;
        },
        resuscitation() {
            return this.form.fluidRequired === fluidRequired.answers.resuscitation;
        },
        displayReplacementFluid() {
            return !this.replacementFluidHidden();
        },
    },
    watch: {
        'form.weightKg': function () {
            this.inputChanged();
        },
        'form.weightSt': function () {
            this.inputChanged();
        },
        'form.weightLb': function () {
            this.inputChanged();
        },
        'form.frailty': function () {
            this.inputChanged();
        },
        'form.potassium': function () {
            this.inputChanged();
        },
        'form.sodium': function () {
            this.inputChanged();
        },
        'form.selectedLosses': function () {
            this.inputChanged();
        },
        'form.selectedIntakes': function () {
            this.inputChanged();
        },
        'form.replacementFluid': function () {
            this.inputChanged();
        }
    },
    // methods that are used as functions
    methods: {
        handleClick(e) {
            if (e.target.matches("a")) {
                let url = e.target.href;
                let target = e.target.target == "_blank" ? "_blank" : "_self";
                let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
                var isnativeapp = document.getElementById("isnativeapp");
                if (isnativeapp === undefined || isnativeapp === null) {
                    window.open(url, target);
                } else {
                    let data = { title: "", nodeid: dataId, href: url, target: target };
                    window.Native("dataCallback", data);
                }
            }
        },
        stPoToKg() {
            if (this.form.weightSt === '' || isNaN(this.form.weightSt)) {
                this.form.weightSt = null;
            }

            if (this.form.weightLb === '') {
                this.form.weightLb = null;
            }
            else if (this.form.weightLb > 13) {
                this.form.weightLb = 13;
            }

            let st = this.form.weightSt;
            let lb = this.form.weightLb;

            if (this.form.weightSt !== null) {
                this.form.weightSt = parseFloat(parseFloat(st).toFixed(0));
                this.form.weightLb = lb != null ? parseFloat(parseFloat(lb).toFixed(0)) : null;

                let kilograms = parseFloat(parseFloat(this.form.weightSt).toFixed(0)) / 0.15747 + parseFloat(parseFloat(isNaN(this.form.weightLb) ? 0 : this.form.weightLb ?? 0).toFixed(0)) / 2.2046;

                //this.setStLb(this.form.weightSt, this.form.weightLb);
                this.setKg(kilograms);
                this.checkValue();
                this.form.weightEdited = true;
            }
            else {
                this.form.weightKg = null;
            }
            return;
        },
        kgToStPo() {
            if (this.form.weightKg === '' || isNaN(this.form.weightKg)) {
                this.form.weightKg = null;
            }

            let stone = parseFloat(
                Math.floor(parseFloat(parseFloat(this.form.weightKg).toFixed(2)) * 0.157473)
            );
            let pounds = parseFloat(
                Math.round(parseFloat(parseFloat(this.form.weightKg).toFixed(2)) * 2.2046) - stone * 14
            );

            if (isNaN(stone)) {
                stone = 0;
            }
            if (pounds >= 14) {
                pounds = 0;
                stone++;
            }

            if (this.form.weightKg !== null) {
                this.setKg(this.form.weightKg);
                this.setStLb(stone, pounds);
                this.checkValue();
                this.form.weightEdited = true;
            }
            else {
                this.form.weightSt = null;
                this.form.weightLb = null;
            }

            return;
        },
        setKg(kilograms) {
            this.form.weightKg = parseFloat(parseFloat(kilograms).toFixed(2));
            return;
        },
        setStLb(stone, pounds) {
            // console.log('current pounds:' + ' ' + pounds);
            this.form.weightSt = parseFloat(parseFloat(stone).toFixed(0));
            if (this.form.weightLb > 13) {
                this.form.weightLb = 13;
            }
            else {
                this.form.weightLb = parseFloat(
                    pounds != null ? parseFloat(parseFloat(pounds).toFixed(0)) : 0
                );
            }
            if (isNaN(pounds) || pounds == null && this.form.weightSt > 0) {
                // console.log("set pounds to 0 as it's empty or null (1)");
                this.form.weightLb = 0;
                // console.log('current pounds set:' + ' ' + pounds);
            }
            return;
        },
        checkValue() {
            if ((this.form.weightKg < weight.warnings.weightRangeMetric.minKg || this.form.weightKg > weight.warnings.weightRangeMetric.maxKg) && this.form.weightKg != "") {
                this.form.weightNotValid = true;
            } else {
                this.form.weightNotValid = false;
            }
            return;
        },
        swapWeightUnit(e) {
            e.preventDefault();
            this.swapToImperial = !this.swapToImperial;
            this.weightButtonText = this.swapToImperial ? this.weight.button.imperialText : this.weight.button.metricText;
        },
        setWeightError(error) {
            this.weightError = error;
            return error;
        },
        inputChanged() {
            this.formSubmitted = false;
        },
        handleIntakesDisplay() {
            var indexOfNone = this.form.selectedIntakes.indexOf(expectedIntakes.noIntake.label);
            if (indexOfNone !== -1) {
                if (indexOfNone === this.form.selectedIntakes.length - 1) {
                    this.form.selectedIntakes = [expectedIntakes.noIntake.label];

                }
                else {
                    this.form.selectedIntakes.splice(indexOfNone, 1);
                }
            }
        },
        handleLossesDisplay() {
            var indexOfNone = this.form.selectedLosses.indexOf(previousLosses.noLoss.label);
            if (indexOfNone !== -1) {
                if (indexOfNone === this.form.selectedLosses.length - 1) {
                    this.form.selectedLosses = [previousLosses.noLoss.label];

                }
                else {
                    this.form.selectedLosses.splice(indexOfNone, 1);
                }
            }
        },
        selectPreviousLoss() {
            this.handleLossesDisplay();

            if (this.form.selectedLosses.indexOf(this.previousLosses.noLoss.label) !== -1) {
                this.form.lossVolume = this.emptyLossesArray();
            }
            else {
                for (let i = 0; i < this.form.lossVolume.length; i++) {
                    var selected = this.form.selectedLosses.indexOf(this.previousLosses.answers[i].label) !== -1
                    this.form.lossVolume[i].selected = selected;
                    if (!selected) {
                        this.form.lossVolume[i].value = null;
                    }
                }
            }
        },
        selectExpectedIntake() {
            this.handleIntakesDisplay();

            if (this.form.selectedIntakes.indexOf(this.expectedIntakes.noIntake.label) !== -1) {
                this.form.intakeVolume = this.emptyIntakesArray();
            }
            else {
                for (let i = 0; i < this.form.intakeVolume.length; i++) {
                    var selected = this.form.selectedIntakes.indexOf(this.expectedIntakes.answers[i].label) !== -1
                    this.form.intakeVolume[i].selected = selected;
                    if (!selected) {
                        this.form.intakeVolume[i].value = null;
                    }
                }
            }
        },
        setClearButton(value) {
            this.clearButton = value;
        },
        replacementFluidHidden() {
            return this.form.selectedLosses.indexOf(this.previousLosses.noLoss.label) === 0 || (this.form.selectedLosses.length == 1 && this.form.selectedLosses.indexOf(results.conditions.vomitingNasogastric) === 0)
        },
        emptyLossesArray: function () {
            var previousLossesLength = this.previousLosses.answers.length;
            var lossesArray = []
            for (let i = 0; i < previousLossesLength; i++) {
                lossesArray.push({ selected: false, value: null });
            }
            return lossesArray;
        },
        emptyIntakesArray: function () {
            var expectedIntakesLength = this.expectedIntakes.answers.length;
            var intakesArray = [];
            for (let i = 0; i < expectedIntakesLength; i++) {
                intakesArray.push({ selected: false, value: null });
            }
            return intakesArray;
        },
        reset() {
            // reset validation
            this.$v.$reset();

            // reset values
            this.form.weightKg = null;
            this.form.weightSt = null;
            this.form.weightLb = null;

            this.form.frailty = null;
            this.form.potassium = null;
            this.form.sodium = null;

            this.form.selectedLosses = [];
            this.form.lossVolume = this.emptyLossesArray();

            this.form.selectedIntakes = [];
            this.form.intakeVolume = this.emptyIntakesArray();

            this.form.replacementFluid = null;

            // reset bools
            this.formSubmitted = false;
            this.errorSummary = false;
            this.isValid = false;
            this.clearButton = false;
            this.form.weightEdited = false;

            // To be hidden from unit testing
            if (process.env.NODE_ENV !== "test") {
                window.scrollTo(0, 0);
            }
        },
        initialiseCalculationValues() {
            //Maintenance Fluid Value Initialisation
            this.maintenanceCalculation.volumeMultiplier = this.form.frailty === frailty.answers.yes ? 20 : 30;
            this.maintenanceCalculation.volumeMaximum = this.form.frailty === frailty.answers.yes ? 1600 : 2400;
            this.maintenanceCalculation.totalIntakeVolume = null;
            this.maintenanceCalculation.fluidType = null;
            this.maintenanceCalculation.fluidVolume = null;
            this.maintenanceCalculation.fluidRate = null;
            this.maintenanceCalculation.fluidDeliveryPeriod = this.hoursInDay;
            this.maintenanceCalculation.alerts = [];

            //Replacement Fluid Value Initialisation
            this.replacementCalculation.volumeMaximum = 3000;
            this.replacementCalculation.totalLossVolume = null;
            this.replacementCalculation.fluidType = null;
            this.replacementCalculation.fluidVolume = null;
            this.replacementCalculation.fluidRate = [];
            this.replacementCalculation.alerts = [];
        },
        calculateFluidIntakeVolume() {
            if (this.form.selectedIntakes.indexOf(expectedIntakes.noIntake.label) === -1) {
                for (let i = 0; i < this.form.intakeVolume.length; i++) {
                    if (this.form.intakeVolume[i].value !== '' && this.form.intakeVolume[i].selected) {
                        this.maintenanceCalculation.totalIntakeVolume += parseInt(this.form.intakeVolume[i].value);
                    }
                }
            }
        },
        calculateFluidLossVolume() {
            this.form.lossVolume.forEach(element => {
                if (element.selected) {
                    this.replacementCalculation.totalLossVolume += parseInt(element.value);
                }
            });
        },
        calculateMaintenanceFluidRate() {
            var fluidRate = this.maintenanceCalculation.fluidVolume / this.hoursInDay;
            this.maintenanceCalculation.fluidRate = Math.round(fluidRate);
        },
        calculateReplacementFluidRate() {
            if (this.form.frailty === frailty.answers.no) {
                this.replacementCalculation.fluidRate.push(results.replacementFluidRate.default);
            }
            else if (this.form.frailty === frailty.answers.yes) {
                this.replacementCalculation.fluidRate.push(results.replacementFluidRate.frailty);
            }
            if (this.replacementCalculation.fluidVolume > this.replacementFluidThreshold) {
                this.replacementCalculation.fluidRate.push(results.replacementFluidRate.volumeThreshold);
            }
        },
        calculateMaintenanceFluidVolume() {
            var fluidVolume = this.form.weightKg * this.maintenanceCalculation.volumeMultiplier;

            if (fluidVolume > this.maintenanceCalculation.volumeMaximum) {
                fluidVolume = this.maintenanceCalculation.volumeMaximum;
            }

            fluidVolume -= this.maintenanceCalculation.totalIntakeVolume;

            fluidVolume = fluidVolume < 0 ? 0 : Math.round(fluidVolume / 100) * 100;

            this.maintenanceCalculation.fluidVolume = fluidVolume;
        },
        calculateReplacementFluidVolume() {
            this.replacementCalculation.fluidVolume = this.replacementCalculation.totalLossVolume;
            if (this.replacementCalculation.fluidVolume > this.replacementCalculation.volumeMaximum) {
                this.replacementCalculation.fluidVolume = this.replacementCalculation.volumeMaximum;
            }

            this.replacementCalculation.fluidVolume = Math.round(this.replacementCalculation.fluidVolume / 100) * 100
        },
        setMaintenanceFluidType() {
            if (this.form.potassium === results.conditions.highPotassium && this.form.sodium === results.conditions.lowSodium) {
                this.maintenanceCalculation.fluidType = results.maintenanceFluidType.both;
            }
            else if (this.form.potassium === results.conditions.highPotassium) {
                this.maintenanceCalculation.fluidType = results.maintenanceFluidType.highPotassium;
            }
            else if (this.form.sodium === results.conditions.lowSodium) {
                this.maintenanceCalculation.fluidType = results.maintenanceFluidType.lowSodium;
            }
            else {
                this.maintenanceCalculation.fluidType = results.maintenanceFluidType.default;
            }
        },
        setReplacementFluidType() {
            this.replacementCalculation.fluidType =
                this.form.selectedLosses.length == 1 && this.form.selectedLosses.indexOf(results.conditions.vomitingNasogastric) === 0
                    ? results.replacementFluidType.vomitingNasogastric
                    : this.form.replacementFluid;
        },
        createMaintenanceAlerts() {
            if (this.form.potassium === results.conditions.lowPotassium) {
                this.maintenanceCalculation.alerts.push(results.alerts.maintenanceLowPotassium);
            }
            else if (this.form.potassium === results.conditions.highPotassium) {
                this.maintenanceCalculation.alerts.push(results.alerts.maintenanceHighPotassium);
            }

            if (this.form.sodium === results.conditions.lowSodium) {
                this.maintenanceCalculation.alerts.push(results.alerts.maintenanceLowSodium);
            }
            else if (this.form.sodium === results.conditions.highSodium) {
                this.maintenanceCalculation.alerts.push(results.alerts.maintenanceHighSodium);
            }

            if (this.form.weightKg > this.weightThreshold) {
                this.maintenanceCalculation.alerts.push(results.alerts.maintenanceWeightThreshold);
            }

            if (this.maintenanceCalculation.totalIntakeVolume > this.maintenanceCalculation.volumeMaximum) {
                if (this.form.frailty === frailty.answers.no) {
                    this.maintenanceCalculation.alerts.push(results.alerts.maintenanceIntakeThreshold);
                }
                else if (this.form.frailty === frailty.answers.yes) {
                    this.maintenanceCalculation.alerts.push(results.alerts.maintenanceIntakeFrailThreshold);
                }
            }
        },
        createReplacementAlerts() {
            if (this.form.potassium === results.conditions.lowPotassium) {
                this.replacementCalculation.alerts.push(results.alerts.replacementLowPotassium);
            }
            if (this.form.sodium === results.conditions.lowSodium) {
                this.replacementCalculation.alerts.push(results.alerts.replacementLowSodium);
            }
            if (this.form.frailty === frailty.answers.yes) {
                this.replacementCalculation.alerts.push(results.alerts.replacementFrail);
            }
            if (this.replacementCalculation.fluidVolume > this.replacementFluidThreshold) {
                this.replacementCalculation.alerts.push(results.alerts.replacementVolumeHigh);
            }
            if (this.replacementCalculation.totalLossVolume > this.replacementCalculation.volumeMaximum) {
                this.replacementCalculation.alerts.push(results.alerts.replacementLossThreshold);
            }
        },
        fluidCalculations() {
            //Initialise Calculation Values
            this.initialiseCalculationValues();

            //Maintenance Fluid Calculations
            this.calculateFluidIntakeVolume();
            this.calculateMaintenanceFluidVolume();
            this.calculateMaintenanceFluidRate();
            this.setMaintenanceFluidType();
            this.createMaintenanceAlerts();

            //Replacement Fluid Calculations
            if (this.form.selectedLosses.indexOf(this.previousLosses.noLoss.label) === -1) {
                this.calculateFluidLossVolume();
                this.setReplacementFluidType();
                this.calculateReplacementFluidVolume();
                this.calculateReplacementFluidRate();
                this.createReplacementAlerts();
            }
        },
        validateForm() {
            this.$v.$touch();
            this.formSubmitted = false;
            const ifAnyError = this.$v.$pending || this.$v.$anyError;
            if (ifAnyError) {
                this.errorSummary = true;
                this.isValid = false;
                if (process.env.NODE_ENV !== "test") {
                    window.scrollTo(0, 0);
                }
            } else {
                this.formSubmitted = true;
                this.isValid = true;
                this.errorSummary = false;
                this.fluidCalculations();
                this.$v.$reset();
            }
        },
    },
};
</script>